import { useState, useEffect } from 'react';
import { 
  Mail, 
  Phone, 
  MessageCircle, 
  Star, 
  Award, 
  MapPin,
  Home,
  ArrowRight,
  Filter,
  Search,
  Loader2,
  TrendingUp,
  Users,
  Briefcase,
  Shield,
  Zap,
  CheckCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { getActiveAgents } from '../lib/agents-api';
import { Agent } from '../types/agent';

interface AgentDirectoryProps {
  onAgentSelect: (agent: Agent) => void;
}

export function AgentDirectory({ onAgentSelect }: AgentDirectoryProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialization, setSelectedSpecialization] = useState<string>('all');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [avatarErrors, setAvatarErrors] = useState<Set<string>>(new Set());
  
  // Fetch real agents on mount
  useEffect(() => {
    async function loadAgents() {
      setLoading(true);
      try {
        const realAgents = await getActiveAgents();
        setAgents(realAgents);
        console.log(`✅ Loaded ${realAgents.length} agents for directory`);
      } catch (error) {
        logger.error('Error loading agents', error as Error, undefined, 'AgentDirectory');
        setAgents([]);
      } finally {
        setLoading(false);
      }
    }
    
    loadAgents();
  }, []);

  // Get unique specializations for filtering
  const allSpecializations = Array.from(
    new Set(agents.flatMap(agent => agent.specializations))
  ).sort();

  // Filter agents based on search and specialization
  const filteredAgents = agents.filter(agent => {
    const matchesSearch =
      (agent.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (agent.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.specializations.some(s => s.toLowerCase().includes(searchTerm.toLowerCase())) ||
      agent.propertyTypes.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesSpecialization =
      selectedSpecialization === 'all' ||
      agent.specializations.includes(selectedSpecialization);

    return matchesSearch && matchesSpecialization;
  });

  const handleWhatsAppClick = (agent: Agent, e: React.MouseEvent) => {
    e.stopPropagation();
    const whatsappNumber = agent.whatsapp.replace(/[^0-9]/g, '');
    const message = encodeURIComponent(`Hi ${agent.name}, I found your profile on Something Property and would like to discuss my property needs.`);
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="container mx-auto px-4 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="mb-4">Meet Our Agents</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Our team of experienced property professionals is here to help you find your dream home 
          or sell your property. Get to know our agents and their areas of expertise.
        </p>
      </div>

      {/* Search and Filter */}
      <div className="mb-8 space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, area, or property type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="md:w-64">
            <select
              value={selectedSpecialization}
              onChange={(e) => setSelectedSpecialization(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-input bg-background"
            >
              <option value="all">All Areas</option>
              {allSpecializations.map(spec => (
                <option key={spec} value={spec}>{spec}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="text-sm text-muted-foreground">
          Showing {filteredAgents.length} of {agents.length} agents
        </div>
      </div>

      {/* Agent Grid */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-12 h-12 mx-auto text-primary mb-4 animate-spin" />
          <h3 className="mb-2">Loading agents...</h3>
          <p className="text-muted-foreground">
            Fetching agent data from database
          </p>
        </div>
      ) : filteredAgents.length === 0 ? (
        <div className="text-center py-12">
          <Filter className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="mb-2">No agents found</h3>
          <p className="text-muted-foreground">
            {agents.length === 0 
              ? 'No agents have been created yet. Add agents via Admin Hub → User Management.'
              : 'Try adjusting your search or filter criteria'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAgents.map((agent) => (
            <Card key={agent.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <CardHeader className="text-center pb-4">
                {/* Avatar */}
                <div className="flex justify-center mb-4">
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-muted flex items-center justify-center flex-shrink-0">
                    {agent.avatar && !avatarErrors.has(agent.id) ? (
                      <img
                        src={agent.avatar}
                        alt={agent.name}
                        className="w-full h-full object-cover"
                        onError={() => setAvatarErrors(prev => new Set([...prev, agent.id]))}
                      />
                    ) : (
                      <span className="text-2xl font-semibold text-muted-foreground">
                        {getInitials(agent.name)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Name and Title */}
                <CardTitle className="mb-1">{agent.name}</CardTitle>
                <CardDescription>{agent.title}</CardDescription>

                {/* Rating and Experience */}
                <div className="flex items-center justify-center gap-4 mt-3">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-medium">{agent.rating}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {agent.yearsExperience} years exp.
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="text-center">
                    <div className="font-semibold">{agent.totalListings}</div>
                    <div className="text-xs text-muted-foreground">Listings</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold">{agent.soldProperties}</div>
                    <div className="text-xs text-muted-foreground">Sold</div>
                  </div>
                </div>

                {/* Areas Served */}
                {agent.areasServed && agent.areasServed.length > 0 && (
                  <div>
                    <div className="text-sm font-medium mb-2 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      Areas
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {agent.areasServed.slice(0, 3).map((area, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {area}
                        </Badge>
                      ))}
                      {agent.areasServed.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{agent.areasServed.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Specialities */}
                {agent.specializations.length > 0 && (
                  <div>
                    <div className="text-sm font-medium mb-2 flex items-center gap-1">
                      <Briefcase className="w-3 h-3" />
                      Specialities
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {agent.specializations.slice(0, 3).map((spec, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {spec}
                        </Badge>
                      ))}
                      {agent.specializations.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{agent.specializations.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Property Types */}
                <div>
                  <div className="text-sm font-medium mb-2 flex items-center gap-1">
                    <Home className="w-3 h-3" />
                    Specialties
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {agent.propertyTypes.slice(0, 2).map((type, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {type}
                      </Badge>
                    ))}
                    {agent.propertyTypes.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        +{agent.propertyTypes.length - 2}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Contact Buttons */}
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <Button
                    data-testid="agent-directory-whatsapp-button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => handleWhatsAppClick(agent, e)}
                    className="w-full"
                  >
                    <MessageCircle className="w-4 h-4 mr-1" />
                    WhatsApp
                  </Button>
                  <Button
                    data-testid="agent-directory-email-button"
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.href = `mailto:${agent.email}`}
                    className="w-full"
                  >
                    <Mail className="w-4 h-4 mr-1" />
                    Email
                  </Button>
                </div>

                {/* View Profile Button */}
                <Button
                  data-testid="agent-directory-view-profile-button"
                  className="w-full"
                  onClick={() => onAgentSelect(agent)}
                >
                  View Profile
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ─── Become an Agent Section ─── */}
      <section className="mt-24 relative">
        {/* Background card */}
        <Card className="overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Image side */}
            <div className="relative aspect-[16/10] lg:aspect-auto min-h-[400px] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/7578899/pexels-photo-7578899.jpeg"
                alt="Real estate professionals – Photo by Kindel Media on Pexels"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent lg:hidden" />
            </div>

            {/* Content side */}
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <Badge variant="outline" className="w-fit mb-4 border-primary/30 text-primary">
                <Briefcase className="w-3 h-3 mr-1" />
                Career Opportunity
              </Badge>

              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Become a Something Property Agent
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Join one of Gauteng's fastest-growing property agencies. We provide the tools,
                training, and support you need to build a successful career in real estate.
              </p>

              {/* Benefits grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {[
                  { icon: TrendingUp, title: 'Competitive Commission', desc: 'Industry-leading commission structure' },
                  { icon: Users, title: 'Mentorship Programme', desc: 'Learn from top-performing agents' },
                  { icon: Zap, title: 'Cutting-Edge Tech', desc: 'CRM, marketing tools & lead gen' },
                  { icon: Shield, title: 'Brand Backing', desc: 'Trusted name in SA property' },
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-sm">{benefit.title}</div>
                      <div className="text-xs text-muted-foreground">{benefit.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Requirements quick list */}
              <div className="mb-8">
                <h4 className="font-semibold text-sm mb-3">What we look for:</h4>
                <div className="space-y-2">
                  {[
                    'Valid NQF 4 Real Estate qualification (or willingness to obtain)',
                    'Strong communication and negotiation skills',
                    'Self-motivated with a passion for property',
                    'Own transport and valid driver\'s licence',
                  ].map((req, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>{req}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  onClick={() => {
                    window.location.href = 'mailto:admin@somethingproperty.co.za?subject=Agent%20Application%20–%20Something%20Property&body=Hi%20Something%20Property%20team%2C%0A%0AI%20am%20interested%20in%20joining%20your%20team%20as%20a%20property%20agent.%0A%0AName%3A%20%0APhone%3A%20%0AArea%3A%20%0AExperience%3A%20%0A%0APlease%20let%20me%20know%20the%20next%20steps.%0A%0AThank%20you!';
                  }}
                  className="group"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Apply Now
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => {
                    window.open('https://wa.me/27615483584?text=' + encodeURIComponent('Hi, I\'m interested in becoming an agent at Something Property. Could you share more details?'), '_blank');
                  }}
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Chat on WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </section>
    </div>
  );
}